import { useState, useEffect } from 'react';
import { CountdownTimer } from './CountdownTimer';
import { SparkleBackground } from './SparkleBackground';
import '@/styles/animations.css';

/**
 * Props for the HeroSection component
 */
interface HeroSectionProps {
  /** Optional CSS class name for container */
  className?: string;
}

/**
 * Floating music symbols decoration for left side
 * SVG music notes with floating animations
 */
function MusicSymbols() {
  const musicNotes = [
    { icon: 'note', x: '5%', y: '20%', size: 32, delay: 0, duration: 6 },
    { icon: 'notes', x: '12%', y: '45%', size: 40, delay: 1.5, duration: 7 },
    { icon: 'note', x: '3%', y: '65%', size: 28, delay: 3, duration: 5.5 },
    { icon: 'notes', x: '15%', y: '30%', size: 24, delay: 2, duration: 8 },
    { icon: 'note', x: '8%', y: '75%', size: 36, delay: 4, duration: 6.5 },
  ];

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {musicNotes.map((note, index) => (
        <div
          key={index}
          className="absolute opacity-20 hover:opacity-40 transition-opacity"
          style={{
            left: note.x,
            top: note.y,
            animation: `float ${note.duration}s ease-in-out infinite, sparkle 3s ease-in-out infinite`,
            animationDelay: `${note.delay}s`,
          }}
        >
          {note.icon === 'note' ? (
            // Single music note
            <svg
              width={note.size}
              height={note.size}
              viewBox="0 0 24 24"
              fill="none"
              stroke="url(#musicGradient)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <defs>
                <linearGradient id="musicGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ffd700" />
                  <stop offset="50%" stopColor="#e94560" />
                  <stop offset="100%" stopColor="#ff8fa3" />
                </linearGradient>
              </defs>
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" fill="url(#musicGradient)" />
              <circle cx="18" cy="16" r="3" fill="url(#musicGradient)" />
            </svg>
          ) : (
            // Double music notes
            <svg
              width={note.size}
              height={note.size}
              viewBox="0 0 24 24"
              fill="none"
              stroke="url(#musicGradient2)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <defs>
                <linearGradient id="musicGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#ff8fa3" />
                  <stop offset="50%" stopColor="#ffd700" />
                  <stop offset="100%" stopColor="#e94560" />
                </linearGradient>
              </defs>
              <path d="M9 18V5l12-2v13M9 9l12-2" />
              <circle cx="6" cy="18" r="3" fill="url(#musicGradient2)" />
              <circle cx="18" cy="16" r="3" fill="url(#musicGradient2)" />
            </svg>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Flowing music staff decoration for right side
 * Treble clef with curved staff lines and music notes
 */
function MusicStaffRight() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Main music staff SVG - positioned on right side */}
      <div
        className="absolute opacity-20 hover:opacity-35 transition-opacity"
        style={{
          right: '2%',
          top: '15%',
          animation: 'float 8s ease-in-out infinite',
        }}
      >
        <svg
          width="280"
          height="320"
          viewBox="0 0 280 320"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="staffGradient" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ffd700" />
              <stop offset="50%" stopColor="#e94560" />
              <stop offset="100%" stopColor="#ff8fa3" />
            </linearGradient>
          </defs>

          {/* Treble Clef (G Clef) */}
          <g transform="translate(20, 180)" style={{ animation: 'float 6s ease-in-out infinite' }}>
            <path
              d="M25 0 C25 -15, 35 -25, 35 -40 C35 -55, 20 -60, 15 -50 C10 -40, 20 -30, 25 -20 C30 -10, 25 5, 20 15 C15 25, 5 30, 5 45 C5 60, 20 65, 30 55 C35 50, 35 40, 25 40 C20 40, 15 45, 20 50"
              stroke="url(#staffGradient)"
              strokeWidth="2.5"
              strokeLinecap="round"
              fill="none"
            />
            <circle cx="20" cy="50" r="6" fill="url(#staffGradient)" opacity="0.6" />
          </g>

          {/* Curved Staff Lines */}
          <path d="M60 220 Q140 180, 220 120 Q260 90, 270 70" stroke="url(#staffGradient)" strokeWidth="1" fill="none" opacity="0.6" />
          <path d="M60 232 Q140 192, 220 132 Q260 102, 270 82" stroke="url(#staffGradient)" strokeWidth="1" fill="none" opacity="0.6" />
          <path d="M60 244 Q140 204, 220 144 Q260 114, 270 94" stroke="url(#staffGradient)" strokeWidth="1" fill="none" opacity="0.6" />
          <path d="M60 256 Q140 216, 220 156 Q260 126, 270 106" stroke="url(#staffGradient)" strokeWidth="1" fill="none" opacity="0.6" />
          <path d="M60 268 Q140 228, 220 168 Q260 138, 270 118" stroke="url(#staffGradient)" strokeWidth="1" fill="none" opacity="0.6" />

          {/* Music Notes on the staff */}
          {/* Quarter note 1 */}
          <g transform="translate(90, 195)">
            <ellipse cx="0" cy="0" rx="7" ry="5" fill="url(#staffGradient)" transform="rotate(-20)" />
            <line x1="6" y1="-2" x2="6" y2="-35" stroke="url(#staffGradient)" strokeWidth="2" />
          </g>

          {/* Eighth note pair (beamed) */}
          <g transform="translate(130, 175)">
            <ellipse cx="0" cy="0" rx="6" ry="4" fill="url(#staffGradient)" transform="rotate(-20)" />
            <line x1="5" y1="-2" x2="5" y2="-30" stroke="url(#staffGradient)" strokeWidth="2" />
            <ellipse cx="25" cy="-10" rx="6" ry="4" fill="url(#staffGradient)" transform="rotate(-20)" />
            <line x1="30" y1="-12" x2="30" y2="-40" stroke="url(#staffGradient)" strokeWidth="2" />
            <path d="M5 -30 Q17 -38, 30 -40" stroke="url(#staffGradient)" strokeWidth="3" fill="none" />
          </g>

          {/* Quarter note 2 */}
          <g transform="translate(185, 145)">
            <ellipse cx="0" cy="0" rx="6" ry="4" fill="url(#staffGradient)" transform="rotate(-20)" />
            <line x1="5" y1="-2" x2="5" y2="-32" stroke="url(#staffGradient)" strokeWidth="2" />
          </g>

          {/* Eighth note with flag */}
          <g transform="translate(220, 120)">
            <ellipse cx="0" cy="0" rx="6" ry="4" fill="url(#staffGradient)" transform="rotate(-20)" />
            <line x1="5" y1="-2" x2="5" y2="-30" stroke="url(#staffGradient)" strokeWidth="2" />
            <path d="M5 -30 Q15 -25, 12 -18" stroke="url(#staffGradient)" strokeWidth="2" fill="none" />
          </g>

          {/* Double eighth notes at top */}
          <g transform="translate(245, 90)">
            <ellipse cx="0" cy="0" rx="5" ry="3.5" fill="url(#staffGradient)" transform="rotate(-20)" />
            <line x1="4" y1="-2" x2="4" y2="-25" stroke="url(#staffGradient)" strokeWidth="1.5" />
            <ellipse cx="18" cy="-8" rx="5" ry="3.5" fill="url(#staffGradient)" transform="rotate(-20)" />
            <line x1="22" y1="-10" x2="22" y2="-33" stroke="url(#staffGradient)" strokeWidth="1.5" />
            <path d="M4 -25 Q13 -32, 22 -33" stroke="url(#staffGradient)" strokeWidth="2.5" fill="none" />
          </g>
        </svg>
      </div>

      {/* Additional floating single notes for depth */}
      <div
        className="absolute opacity-15"
        style={{
          right: '18%',
          top: '60%',
          animation: 'float 7s ease-in-out infinite reverse',
          animationDelay: '2s',
        }}
      >
        <svg width="35" height="50" viewBox="0 0 35 50" fill="none">
          <ellipse cx="10" cy="40" rx="9" ry="6" fill="url(#noteGradient1)" transform="rotate(-15 10 40)" />
          <line x1="18" y1="37" x2="18" y2="5" stroke="url(#noteGradient1)" strokeWidth="2.5" />
          <defs>
            <linearGradient id="noteGradient1" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#ff8fa3" />
              <stop offset="100%" stopColor="#ffd700" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Another floating note */}
      <div
        className="absolute opacity-12"
        style={{
          right: '8%',
          top: '75%',
          animation: 'float 5s ease-in-out infinite',
          animationDelay: '1s',
        }}
      >
        <svg width="30" height="45" viewBox="0 0 30 45" fill="none">
          <ellipse cx="8" cy="35" rx="7" ry="5" fill="url(#noteGradient2)" transform="rotate(-15 8 35)" />
          <line x1="14" y1="32" x2="14" y2="5" stroke="url(#noteGradient2)" strokeWidth="2" />
          <path d="M14 5 Q24 10, 20 20" stroke="url(#noteGradient2)" strokeWidth="2" fill="none" />
          <defs>
            <linearGradient id="noteGradient2" x1="0%" y1="100%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#e94560" />
              <stop offset="100%" stopColor="#ffd700" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
}

/**
 * Floating particle component for background decoration
 * Enhanced with romantic gold and rose gradient orbs
 */
function FloatingParticles() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Decorative particles with gold shimmer */}
      <div className="hero-particle" />
      <div className="hero-particle" />
      <div className="hero-particle" />
      <div className="hero-particle" />
      <div className="hero-particle" />

      {/* Large gradient orbs - romantic palette */}
      <div
        className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-20 animate-float"
        style={{
          background: 'radial-gradient(circle, rgba(255, 215, 0, 0.3) 0%, rgba(233, 69, 96, 0.2) 50%, transparent 70%)',
          filter: 'blur(40px)',
          animationDuration: '8s',
        }}
      />
      <div
        className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full opacity-15"
        style={{
          background: 'radial-gradient(circle, rgba(255, 143, 163, 0.3) 0%, rgba(233, 69, 96, 0.2) 50%, transparent 70%)',
          filter: 'blur(60px)',
          animation: 'float 10s ease-in-out infinite reverse',
        }}
      />
      {/* Additional gold accent orb */}
      <div
        className="absolute top-1/3 -right-16 w-72 h-72 rounded-full opacity-10"
        style={{
          background: 'radial-gradient(circle, rgba(247, 231, 206, 0.4) 0%, transparent 70%)',
          filter: 'blur(50px)',
          animation: 'float 12s ease-in-out infinite',
        }}
      />
    </div>
  );
}

/**
 * School emblem/logo component with romantic gold accents
 */
function SchoolEmblem() {
  return (
    <div
      className="inline-flex items-center justify-center w-20 h-20 md:w-24 md:h-24 mb-6 rounded-full animate-scale-in"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.15) 0%, rgba(233, 69, 96, 0.15) 50%, rgba(255, 143, 163, 0.1) 100%)',
        border: '2px solid rgba(255, 215, 0, 0.4)',
        boxShadow: '0 0 30px rgba(255, 215, 0, 0.2), 0 0 60px rgba(233, 69, 96, 0.15)',
      }}
    >
      <span
        className="text-3xl md:text-4xl font-display font-bold"
        style={{
          background: 'linear-gradient(135deg, #ffd700 0%, #e94560 50%, #ff8fa3 100%)',
          WebkitBackgroundClip: 'text',
          backgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        96
      </span>
    </div>
  );
}

/**
 * Reunion badge component with romantic gold accents
 */
function ReunionBadge() {
  return (
    <div
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 opacity-0 animate-fade-in-down animation-delay-200"
      style={{
        background: 'linear-gradient(90deg, rgba(255, 215, 0, 0.15) 0%, rgba(233, 69, 96, 0.15) 50%, rgba(255, 143, 163, 0.1) 100%)',
        border: '1px solid rgba(255, 215, 0, 0.3)',
        boxShadow: '0 0 20px rgba(255, 215, 0, 0.1)',
      }}
    >
      <span
        className="w-2 h-2 rounded-full animate-pulse"
        style={{ background: 'linear-gradient(135deg, #ffd700 0%, #ff8fa3 100%)' }}
      />
      <span className="text-sm font-medium text-white/80 uppercase tracking-wider">
        30th Anniversary Reunion
      </span>
      <span className="animate-sparkle" style={{ fontSize: '10px' }}>✨</span>
    </div>
  );
}

/**
 * Animated title sequence configuration
 */
const TITLE_SEQUENCE = [
  'Re:New',
  'Re:Connect',
  'Re:Joice',
  'Re:Discover',
  'Re:Member',
  'Re:Celebrate',
  'Re:Unite',
  'Re:Live',
  'Re:Nav-96',
];

const TITLE_INTERVAL = 2000; // ms between each title change

/**
 * Main title component with animated title cycling through Re:xyz names
 */
function HeroTitle() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);

  useEffect(() => {
    // If we've reached the final title, stop the animation
    if (currentIndex >= TITLE_SEQUENCE.length - 1) {
      setIsAnimationComplete(true);
      return;
    }

    const timer = setTimeout(() => {
      setCurrentIndex((prev) => prev + 1);
    }, TITLE_INTERVAL);

    return () => clearTimeout(timer);
  }, [currentIndex]);

  const currentTitle = TITLE_SEQUENCE[currentIndex];

  return (
    <h1 className="animate-hero-title">
      <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-display font-bold mb-2">
        <span
          key={currentTitle}
          className={`inline-block ${isAnimationComplete ? 'animate-hero-glow' : ''}`}
          style={{
            background: 'linear-gradient(135deg, #ffd700 0%, #e94560 25%, #ff8fa3 50%, #e94560 75%, #ffd700 100%)',
            backgroundSize: '200% 200%',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            animation: isAnimationComplete
              ? 'gradientRotate 4s ease infinite, heroGlow 2s ease-in-out infinite'
              : 'gradientRotate 4s ease infinite, titleCycleIn 0.8s cubic-bezier(0.16, 1, 0.3, 1) both',
          }}
        >
          {currentTitle}
        </span>
      </span>
      <span
        className="block text-xl sm:text-2xl md:text-3xl font-display text-white/90 mt-4 opacity-0 animate-fade-in animation-delay-400"
      >
        Navarasam School 1996 Batch
      </span>
    </h1>
  );
}

/**
 * Tagline/subtitle component with static message
 */
function HeroTagline() {
  return (
    <div className="animate-hero-subtitle mt-6">
      {/* Static message */}
      <p className="max-w-2xl mx-auto text-base sm:text-lg md:text-xl text-white/70 leading-relaxed">
        Three decades of memories. A lifetime of friendships.
        <br className="hidden sm:block" />
        <span
          className="font-medium"
          style={{
            background: 'linear-gradient(90deg, #ffd700 0%, #ff8fa3 50%, #e94560 100%)',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Join us as we reunite and celebrate.
        </span>
      </p>
    </div>
  );
}


/**
 * Stats/highlights section below hero with romantic accent colors
 */
function HeroStats() {
  const stats = [
    { value: '30', label: 'Years', color: '#ffd700' },
    { value: '96', label: 'Batch', color: '#e94560' },
    { value: '1', label: 'Epic Reunion', color: '#ff8fa3' },
  ];

  return (
    <div
      className="flex flex-wrap items-center justify-center gap-8 md:gap-12 mt-12 pt-12 border-t opacity-0 animate-fade-in-up animation-delay-700"
      style={{ borderColor: 'rgba(255, 215, 0, 0.15)' }}
    >
      {stats.map((stat, index) => (
        <div
          key={stat.label}
          className="text-center group"
          style={{ animationDelay: `${700 + index * 100}ms` }}
        >
          <div
            className="text-3xl md:text-4xl font-display font-bold transition-all duration-300 group-hover:scale-110"
            style={{ color: stat.color }}
          >
            {stat.value}
          </div>
          <div className="text-sm text-white/60 uppercase tracking-wider mt-1">
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * HeroSection Component
 *
 * A vibrant, animated hero section for the Re:Nav-96 reunion website.
 * Features the countdown timer, branding, and call-to-action buttons.
 *
 * Features:
 * - Animated gradient background with romantic colors
 * - Sparkling particle effects (gold, rose, champagne)
 * - Floating decorative particles
 * - Rotating tagline carousel
 * - Staggered entrance animations
 * - Countdown timer integration
 * - Responsive design
 * - Accessible with reduced-motion support
 *
 * @example
 * ```tsx
 * <HeroSection />
 *
 * <HeroSection className="min-h-screen" />
 * ```
 */
export function HeroSection({ className = '' }: HeroSectionProps) {
  return (
    <section
      className={`relative min-h-[90vh] flex flex-col items-center justify-center py-16 px-4 overflow-hidden hero-bg-animated ${className}`}
    >
      {/* Sparkle background overlay */}
      <SparkleBackground density="medium" className="z-0" />

      {/* Background decorations */}
      <FloatingParticles />

      {/* Music symbols on the left */}
      <MusicSymbols />

      {/* Music staff with notes on the right */}
      <MusicStaffRight />

      {/* Content container */}
      <div className="relative z-10 max-w-5xl mx-auto text-center">
        {/* School emblem */}
        <SchoolEmblem />

        {/* Reunion badge */}
        <ReunionBadge />

        {/* Main title */}
        <HeroTitle />

        {/* Tagline */}
        <HeroTagline />

        {/* Countdown timer */}
        <div
          className="mt-6 max-w-3xl mx-auto opacity-0 animate-scale-in animation-delay-600"
        >
          <CountdownTimer />
        </div>

        {/* Stats */}
        <HeroStats />
      </div>

      {/* Scroll indicator with gold accent */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 opacity-0 animate-fade-in animation-delay-800"
        aria-hidden="true"
      >
        <div
          className="w-6 h-10 rounded-full flex items-start justify-center p-1"
          style={{ border: '2px solid rgba(255, 215, 0, 0.3)' }}
        >
          <div
            className="w-1.5 h-3 rounded-full"
            style={{
              background: 'linear-gradient(180deg, #ffd700 0%, #e94560 100%)',
              animation: 'float 1.5s ease-in-out infinite',
            }}
          />
        </div>
        <p className="text-xs text-white/40 mt-2 uppercase tracking-wider">Scroll</p>
      </div>
    </section>
  );
}

export default HeroSection;
